import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'components/ui/sonner';
import { TooltipProvider } from 'components/ui/tooltip';
import { InstallAppButton } from 'components/InstallAppButton';
import { AuthProvider, useAuth } from 'contexts/AuthContext';
import { LanguageProvider } from 'lib/i18n';
import './App.css';

import LandingPage from 'pages/LandingPage';
import LoginPage from 'pages/LoginPage';
import RegisterPage from 'pages/RegisterPage';
import ForgotPasswordPage from 'pages/ForgotPasswordPage';
import ResetPasswordPage from 'pages/ResetPasswordPage';
import TermsPage from 'pages/TermsPage';
import HowItWorksPage from 'pages/HowItWorksPage';
import DashboardPage from 'pages/DashboardPage';
import CreateDeliveryPage from 'pages/CreateDeliveryPage';
import CreateTripPage from 'pages/CreateTripPage';
import ListingsPage from 'pages/ListingsPage';
import DeliveryDetailPage from 'pages/DeliveryDetailPage';
import TripDetailPage from 'pages/TripDetailPage';
import MissionDetailPage from 'pages/MissionDetailPage';
import MissionsPage from 'pages/MissionsPage';
import MessagesPage from 'pages/MessagesPage';
import ProfilePage from 'pages/ProfilePage';
import PublicProfilePage from 'pages/PublicProfilePage';
import WalletPage from 'pages/WalletPage';
import VehiclesPage from 'pages/VehiclesPage';
import DocumentsPage from 'pages/DocumentsPage';
import SearchPage from 'pages/SearchPage';
import SavedSearchesPage from 'pages/SavedSearchesPage';
import SupportPage from 'pages/SupportPage';
import TrackingPage from 'pages/TrackingPage';
import AdminDashboardPage from 'pages/admin/AdminDashboardPage';
import AdminUsersPage from 'pages/admin/AdminUsersPage';
import AdminCampaignsPage from 'pages/admin/AdminCampaignsPage';
import AdminDiscountCodesPage from 'pages/admin/AdminDiscountCodesPage';
import UnsubscribePage from 'pages/UnsubscribePage';
import { AdminMissionsPage, AdminDisputesPage, AdminReportsPage, AdminValidationsPage, AdminDeliveriesPage, AdminTripsPage, AdminSupportPage, AdminPayoutsPage, AdminAuditLogPage } from 'pages/admin/AdminOtherPages';

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  if (!user) {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return children;
};

const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/track/:trackingToken" element={<TrackingPage />} />
      <Route path="/search" element={<Protected><SearchPage /></Protected>} />
      <Route path="/saved-searches" element={<Protected><SavedSearchesPage /></Protected>} />

      {/* Auth user */}
      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/deliveries/new" element={<Protected><CreateDeliveryPage /></Protected>} />
      <Route path="/deliveries" element={<Protected><ListingsPage kind="deliveries" mineDefault={true} /></Protected>} />
      <Route path="/deliveries/browse" element={<Protected><ListingsPage kind="deliveries" mineDefault={false} /></Protected>} />
      <Route path="/deliveries/:id" element={<Protected><DeliveryDetailPage /></Protected>} />

      <Route path="/trips/new" element={<Protected><CreateTripPage /></Protected>} />
      <Route path="/trips" element={<Protected><ListingsPage kind="trips" mineDefault={true} /></Protected>} />
      <Route path="/trips/search" element={<Protected><ListingsPage kind="trips" mineDefault={false} /></Protected>} />
      <Route path="/trips/:id" element={<Protected><TripDetailPage /></Protected>} />

      <Route path="/missions" element={<Protected><MissionsPage /></Protected>} />
      <Route path="/missions/:id" element={<Protected><MissionDetailPage /></Protected>} />

      <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
      <Route path="/messages/:otherId" element={<Protected><MessagesPage /></Protected>} />

      <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/users/:userId" element={<Protected><PublicProfilePage /></Protected>} />
      <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
      <Route path="/vehicles" element={<Protected><VehiclesPage /></Protected>} />
      <Route path="/documents" element={<Protected><DocumentsPage /></Protected>} />
      <Route path="/support" element={<Protected><SupportPage /></Protected>} />

      {/* Admin */}
      <Route path="/admin" element={<AdminOnly><AdminDashboardPage /></AdminOnly>} />
      <Route path="/admin/users" element={<AdminOnly><AdminUsersPage /></AdminOnly>} />
      <Route path="/admin/campaigns" element={<AdminOnly><AdminCampaignsPage /></AdminOnly>} />
      <Route path="/admin/discount-codes" element={<AdminOnly><AdminDiscountCodesPage /></AdminOnly>} />
      <Route path="/admin/deliveries" element={<AdminOnly><AdminDeliveriesPage /></AdminOnly>} />
      <Route path="/admin/trips" element={<AdminOnly><AdminTripsPage /></AdminOnly>} />
      <Route path="/admin/missions" element={<AdminOnly><AdminMissionsPage /></AdminOnly>} />
      <Route path="/admin/disputes" element={<AdminOnly><AdminDisputesPage /></AdminOnly>} />
      <Route path="/admin/reports" element={<AdminOnly><AdminReportsPage /></AdminOnly>} />
      <Route path="/admin/support" element={<AdminOnly><AdminSupportPage /></AdminOnly>} />
      <Route path="/admin/payouts" element={<AdminOnly><AdminPayoutsPage /></AdminOnly>} />
      <Route path="/admin/audit-logs" element={<AdminOnly><AdminAuditLogPage /></AdminOnly>} />
      <Route path="/admin/validations" element={<AdminOnly><AdminValidationsPage /></AdminOnly>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <TooltipProvider>
          <AuthProvider>
            <LanguageProvider>
              <AppRoutes />
              <InstallAppButton />
              <Toaster position="top-right" richColors />
            </LanguageProvider>
          </AuthProvider>
        </TooltipProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
